import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hexagon, Play, ArrowRight, Activity, Route, TrendingUp, X } from "lucide-react";
import { ROLE_VIEWS } from "@/lib/views";

const ENTRIES = [
  { key: "live", label: "Show Live Operation", desc: "Runs the Cause → Decision → Action → Outcome simulation end to end.", to: "/live?autorun=1", icon: Activity, testId: "entry-live" },
  { key: "journey", label: "Show Client Journey", desc: "Opens Maria Rossi — the complete €2,500 funding recovery story.", to: "/views/journey?client=ftd", icon: Route, testId: "entry-journey" },
  { key: "impact", label: "Show Executive Impact", desc: "Opens the ROI, leakage and attribution dashboard.", to: "/impact", icon: TrendingUp, testId: "entry-impact" },
];

export default function Presentation() {
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("onet_present", "1");
    window.dispatchEvent(new Event("onet-present"));
  }, []);

  const exit = () => {
    localStorage.removeItem("onet_present");
    window.dispatchEvent(new Event("onet-present"));
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="presentation-page">
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Hexagon className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          </div>
          <span className="font-display font-bold text-zinc-100 text-sm">O.N.E.Tech</span>
          <span className="text-[9px] font-mono text-amber-500/90 border border-amber-500/20 bg-amber-500/5 rounded-sm px-2 py-0.5 ml-2">SYNTHETIC DEMONSTRATION ENVIRONMENT</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="presentation-view-selector">
          {ROLE_VIEWS.map((v) => (
            <Link key={v.key} to={v.to} data-testid={`present-view-${v.key}`}
              className="px-2.5 py-1.5 rounded-sm text-[11px] font-semibold text-zinc-400 hover:text-emerald-400 hover:bg-zinc-900 border border-transparent hover:border-zinc-800 transition-colors duration-150">
              {v.label}
            </Link>
          ))}
          <button onClick={exit} data-testid="exit-presentation"
            className="ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200">
            <X className="w-3.5 h-3.5" /> Exit
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-3xl w-full space-y-8">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-3">Presentation Mode</div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-100 font-display">O.N.E.TECH</h1>
            <p className="text-lg text-zinc-400 mt-2">Brokerage Operational Intelligence Infrastructure</p>
            <div className="mt-5 space-y-1.5 text-sm text-zinc-300">
              {[
                "Connected to the systems brokers already use.",
                "Detecting lifecycle leakage.",
                "Triggering governed action.",
                "Coordinating human teams.",
                "Measuring attributable outcomes.",
              ].map((l) => (
                <div key={l} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {l}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="presentation-run-live-demo"
              onClick={() => navigate("/live?autorun=1")}
              className="flex items-center gap-2 px-5 py-3 rounded-sm text-sm font-bold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200 shadow-[0_0_15px_rgba(16,185,129,0.25)] hover:shadow-[0_0_25px_rgba(16,185,129,0.45)]"
            >
              <Play className="w-4 h-4" strokeWidth={2} /> RUN LIVE DEMO
            </button>
            <span className="text-[10px] font-mono text-zinc-600 tracking-widest uppercase">Cause → Decision → Action → Outcome</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ENTRIES.map((e) => (
              <button
                key={e.key}
                data-testid={e.testId}
                onClick={() => navigate(e.to)}
                className="text-left bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 rounded-md p-4 transition-colors duration-200 group"
              >
                <e.icon className="w-5 h-5 text-emerald-400 mb-2" strokeWidth={1.5} />
                <div className="text-xs font-semibold text-zinc-100 flex items-center justify-between">
                  {e.label}
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-emerald-400 transition-colors duration-200" />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{e.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
